function titleCasePart(value: string): string {
  const lower = value.toLocaleLowerCase('ru-RU')
  return lower.slice(0, 1).toLocaleUpperCase('ru-RU') + lower.slice(1)
}

export function isPlaceholderCustomerName(value?: string): boolean {
  if (!value) return true
  const normalized = value.normalize('NFKC').trim().toLocaleLowerCase('ru-RU')
  return /^(покупатель|клиент)(?:\s*[·•—–-]\s*.+|\s+.+)?$/.test(normalized)
}

/** Names copied from marketplace contact fields must never become a person label. */
export function isHumanCustomerName(value?: string): boolean {
  if (!value || isPlaceholderCustomerName(value)) return false
  const normalized = value.normalize('NFKC').trim()
  return Boolean(normalized) && !normalized.includes('@') && !/\d/.test(normalized)
}

export function customerNameScore(value?: string): number {
  if (!isHumanCustomerName(value)) return 0
  const normalized = value!.normalize('NFKC').trim()
  const words = normalized.split(/\s+/).filter(Boolean)
  const letters = (normalized.match(/[a-zа-яё]/gi) || []).length
  return (words.length >= 2 && words.length <= 4 ? 100 : 30) + letters
}

/** Select a person label deterministically, independent of source/resync order. */
export function chooseCustomerName(current?: string, candidate?: string): string | undefined {
  if (!current) return candidate
  if (!candidate) return current
  const scoreDifference = customerNameScore(candidate) - customerNameScore(current)
  if (scoreDifference !== 0) return scoreDifference > 0 ? candidate : current
  return candidate.normalize('NFKC').trim().localeCompare(current.normalize('NFKC').trim(), 'ru-RU') < 0
    ? candidate
    : current
}

export function formatCustomerName(value?: string): string {
  if (!value) return 'Покупатель'
  return value
    .normalize('NFKC')
    .trim()
    .split(/\s+/)
    .map((word) => word.split('-').map(titleCasePart).join('-'))
    .join(' ')
}

export function compactCustomerName(value?: string, maxLength = 34): string {
  const formatted = formatCustomerName(value)
  const words = formatted.split(/\s+/)
  if (formatted.length <= maxLength || words.length < 3) return formatted

  // Russian customer data normally arrives as surname/name/patronymic or
  // name/surname/patronymic. In both forms the patronymic is the last token.
  const withoutPatronymic = words.slice(0, -1).join(' ')
  return withoutPatronymic || formatted
}

export function customerInitials(value?: string): string {
  const words = compactCustomerName(value)
    .split(/\s+/)
    .filter(Boolean)
  if (words.length === 0) return 'П'
  const selected = words.length === 1 ? words : words.slice(0, 2)
  return selected
    .map((word) => word.slice(0, 1).toLocaleUpperCase('ru-RU'))
    .join('')
}
